import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Users, Wallet, FileDown, Lock, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Read-only salary view for Franchise Owners.
 * Reads exclusively from the locked payroll_snapshot (never recalculated).
 * Server-side gate: (payroll exists) + (visibility ON) + (month in window)
 * + (own centre only).
 */
export const SalaryManpowerCostSection = ({ session, defaultMonth }) => {
  const isSA = !!(session?.is_super_admin || session?.is_admin
                     || (session?.roles || []).includes?.("accounts")
                     || session?.role_key === "accountant");
  const [month, setMonth] = useState(defaultMonth || new Date().toISOString().slice(0, 7));
  const [pickedCenter, setPickedCenter] = useState("");
  const [centers, setCenters] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load centre list for SA/Accounts preview picker.
  useEffect(() => {
    if (!isSA) return;
    fetch(`${API}/api/centers`).then((r) => r.json()).then((j) => {
      const list = (j.centers || j || []).filter((c) => (c.code || "") !== "PB-MGT");
      setCenters(list);
    }).catch(() => {});
  }, [isSA]);

  const load = useCallback(async () => {
    if (!session?.token || !month) return;
    setLoading(true);
    try {
      const body = { token: session.token, month };
      if (isSA && pickedCenter) body.center = pickedCenter;
      const res = await fetch(`${API}/api/salary-visibility/owner-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await res.json();
      setData(j);
    } catch {
      toast.error('Failed to load salary details');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [session, month, isSA, pickedCenter]);

  useEffect(() => { load(); }, [load]);

  const download = async (kind) => {
    if (!session?.token || !month) return;
    try {
      const body = { token: session.token, month };
      if (isSA && pickedCenter) body.center = pickedCenter;
      const res = await fetch(`${API}/api/salary-visibility/owner-view-${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Download failed');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Salary_${data?.center || 'centre'}_${month}.${kind === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const t = data?.totals || {};
  const currency = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-4" data-testid="fo-salary-section">
      <Card className="bg-white border-slate-200/60">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-700" /> Salary &amp; Manpower Cost
            <Badge variant="outline" className="ml-2 text-[10px]">Read Only</Badge>
            <Badge className="text-[10px] bg-slate-100 text-slate-700"><Lock className="w-3 h-3 mr-1" /> Locked Snapshot</Badge>
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Salary numbers below are the exact figures that were finalised and paid for the selected month. Historical values never change even if employees transfer later.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Month</label>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-40"
                data-testid="fo-salary-month"
              />
            </div>
            {isSA && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Preview Centre (SA)</label>
                <select
                  value={pickedCenter}
                  onChange={(e) => setPickedCenter(e.target.value)}
                  className="h-9 px-2 rounded border border-slate-300 text-sm bg-white"
                  data-testid="sa-preview-center"
                >
                  <option value="">(my own)</option>
                  {centers.map((c) => (
                    <option key={c.code} value={c.code}>{c.code}</option>
                  ))}
                </select>
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => download('excel')}
              disabled={!data?.visible}
              data-testid="fo-salary-dl-excel"
            >
              <FileDown className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => download('pdf')}
              disabled={!data?.visible}
              data-testid="fo-salary-dl-pdf"
            >
              <FileDown className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>

          {loading && (
            <p className="py-8 text-center text-slate-500 text-sm" data-testid="fo-salary-loading">Loading…</p>
          )}

          {!loading && data && !data.visible && data.reason === 'SALARY NOT RELEASED BY MANAGEMENT' && (
            <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/60 text-amber-900 text-sm" data-testid="fo-salary-hidden">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="font-semibold mb-1">Salary not released by Management</p>
                  <p className="text-xs">
                    Salary Visibility for this centre and month has not been authorised.
                    Please contact your Franchisor or the Accounts team.
                  </p>
                  {data.detail && <p className="text-[11px] mt-1 text-amber-800/80">{data.detail}</p>}
                </div>
              </div>
            </div>
          )}

          {!loading && data && !data.visible && data.reason === 'SALARY FOR THIS MONTH HAS NOT YET BEEN FINALISED' && (
            <div className="p-4 rounded-lg border border-sky-200 bg-sky-50/60 text-sky-900 text-sm" data-testid="fo-salary-not-finalised">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="font-semibold mb-1">Salary for this month has not yet been finalised</p>
                  <p className="text-xs">
                    This month is authorised for viewing, but the payroll has not been closed by Management yet.
                    Please check back once payroll has been finalised.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!loading && data && !data.visible &&
            data.reason !== 'SALARY NOT RELEASED BY MANAGEMENT' &&
            data.reason !== 'SALARY FOR THIS MONTH HAS NOT YET BEEN FINALISED' && (
            <div className="p-4 rounded-lg border border-slate-200 bg-slate-50 text-slate-800 text-sm" data-testid="fo-salary-blocked-other">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="font-semibold mb-1">Salary details unavailable</p>
                  <p className="text-xs">{data.reason}</p>
                </div>
              </div>
            </div>
          )}

          {!loading && data?.visible && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> Employees</p>
                  <p className="text-lg font-semibold text-slate-800" data-testid="fo-salary-tile-employees">{t.total_employees || 0}</p>
                </div>
                <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-700">Gross</p>
                  <p className="text-lg font-semibold text-emerald-900" data-testid="fo-salary-tile-gross">{currency(t.total_gross)}</p>
                </div>
                <div className="p-3 rounded-lg border border-orange-200 bg-orange-50">
                  <p className="text-[10px] uppercase tracking-wide text-orange-700">Advance</p>
                  <p className="text-lg font-semibold text-orange-900" data-testid="fo-salary-tile-advance">{currency(t.total_advance)}</p>
                </div>
                <div className="p-3 rounded-lg border border-red-200 bg-red-50">
                  <p className="text-[10px] uppercase tracking-wide text-red-700">Deductions</p>
                  <p className="text-lg font-semibold text-red-900" data-testid="fo-salary-tile-deductions">{currency(t.total_deductions)}</p>
                </div>
                <div className="p-3 rounded-lg border border-blue-200 bg-blue-50">
                  <p className="text-[10px] uppercase tracking-wide text-blue-700">Net Salary</p>
                  <p className="text-lg font-semibold text-blue-900" data-testid="fo-salary-tile-net">{currency(t.total_net)}</p>
                </div>
              </div>

              {/* iter176 — MGT Salary Allocation tile (July 2026+ eligible India centres) */}
              {data?.mgt_allocation?.eligible && data.mgt_allocation.selected_center_included && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-lg border border-purple-200 bg-purple-50" data-testid="fo-mgt-allocation-tile">
                    <p className="text-[10px] uppercase tracking-wide text-purple-700">MGT Salary Allocation</p>
                    <p className="text-lg font-semibold text-purple-900" data-testid="fo-mgt-allocation-amount">
                      {currency(data.mgt_allocation.selected_center_amount)}
                    </p>
                    <p className="text-[10px] text-purple-800/70 mt-1">
                      Share of PB-MGT central team salary attributed to your centre this month.
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border border-indigo-200 bg-indigo-50" data-testid="fo-total-manpower-cost-tile">
                    <p className="text-[10px] uppercase tracking-wide text-indigo-700">Total Manpower Cost</p>
                    <p className="text-lg font-semibold text-indigo-900" data-testid="fo-total-manpower-cost-amount">
                      {currency(Number(t.total_gross || 0)
                                    + Number(data.mgt_allocation.selected_center_amount || 0))}
                    </p>
                    <p className="text-[10px] text-indigo-800/70 mt-1">
                      Gross salary + MGT allocation (attributed cost).  Advances not deducted.
                    </p>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="text-xs">Employee</TableHead>
                      <TableHead className="text-xs">Designation</TableHead>
                      <TableHead className="text-xs">Centre</TableHead>
                      <TableHead className="text-xs text-right">Working Days</TableHead>
                      <TableHead className="text-xs text-right">Paid Days</TableHead>
                      <TableHead className="text-xs text-right">Monthly Salary</TableHead>
                      <TableHead className="text-xs text-right">Gross</TableHead>
                      <TableHead className="text-xs text-right">Advance</TableHead>
                      <TableHead className="text-xs text-right">Net Salary</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.rows || []).map((r, i) => (
                      <TableRow key={i} data-testid={`fo-salary-row-${i}`}>
                        <TableCell className="text-xs font-medium">{r.employeeName}</TableCell>
                        <TableCell className="text-xs">{r.designation || '—'}</TableCell>
                        <TableCell className="text-xs">{r.payrollCenter}</TableCell>
                        <TableCell className="text-xs text-right">{r.daysInMonth}</TableCell>
                        <TableCell className="text-xs text-right">{r.presentDays}</TableCell>
                        <TableCell className="text-xs text-right">{currency(r.monthlySalary)}</TableCell>
                        <TableCell className="text-xs text-right">{currency(r.grossSalary)}</TableCell>
                        <TableCell className="text-xs text-right">{currency(r.advance)}</TableCell>
                        <TableCell className="text-xs text-right font-semibold">{currency(r.netSalary)}</TableCell>
                      </TableRow>
                    ))}
                    {(data.rows || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-6 text-slate-400 text-xs">
                          No employees on record for this centre in {month}.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              {data?.generated_at && (
                <p className="mt-2 text-[10px] text-slate-400">
                  Payroll snapshot locked on {String(data.generated_at).slice(0, 19).replace('T', ' ')} UTC.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SalaryManpowerCostSection;
